import { ROLES, type Continuity } from '../../packages/contracts/src/index.ts';
import { hash, canonical, killPerson, bindLiving, registerPerson, allocateItem, transferItem, bequeath, promote, demanifest, reviseSurvivor, validateLedger, assertContinuityTransition, changeRemains, returnTransition, settlementAccount, assertCompleteAccount, settlementEligibility, settle, nextRunOrdinal, FIRST_EMBER, formatDate, INTERVAL_DAYS, createRun, makeEnvelope, reduceIntent, strategicHash, recordDeath, solveEvidence, executeWitness } from '../../packages/sim/src/index.ts';
import { ledgerFixture, earnedHistory, opportunity } from '../fixtures/contracts/factories.ts';

function assert(condition: unknown, message: string): asserts condition { if (!condition) throw new Error(message); }
function equal(a: unknown, b: unknown, message: string): void { assert(canonical(a) === canonical(b), message); }
function rejects(action: () => unknown, message: string): string {
  try { action(); } catch (e) { return e instanceof Error ? e.message : String(e); }
  throw new Error(`Invalid case escaped detection: ${message}`);
}
export const COVERAGE = [
  ['CT01', 'Dead people never return', 'D02 companion/relative/operator bindings; D03–D05 live legacy and leader consumers'],
  ['CT02', 'Survivors keep their actual current state', 'D01 item/skill systems; D03 played legacy encounters'],
  ['CT03', 'The bequest is one object in one place', 'D03 full closure UI; D09 durable interruption recovery'],
  ['CT04', 'An absent survivor is still alive', 'D03 live placement; D08 all archived survivors'],
  ['CT05', 'Threats do not cause or stop the Return', 'D04 ordinary campaign; D07 all threat families'],
  ['CT06', 'Only authored commitments change politics', 'D01 pause/trade gameplay; D04 campaign beats'],
  ['CT07', 'A promised investigation has a feasible route', 'D02 actual dialogue/navigation/costs; D08 full evidence fairness'],
  ['CT08', 'First Ember must be earned over closed lives', 'D03 persisted provenance; D08 fresh four-run legal play'],
  ['CT09', 'Every living survivor belongs in the account', 'D03 registry consumers; D08 full settlement transaction and 10,000-person stress'],
  ['CT10', 'The next date is 1 Ember 641 AC', 'D08 playable epilogue; D09 release snapshot'],
] as const;
export type InvariantResult = { id: string; title: string; valid: boolean; invalid: boolean; detail: string; outstanding: string };
const checks: Record<string, () => string> = {
  CT01() {
    let c = ledgerFixture();
    c = killPerson(c, 'person:ilan', 'death:canal');
    validateLedger(c);
    for (const role of ROLES) rejects(() => bindLiving(c, 'person:ilan', role, 'Z04'), role);
    const alias = structuredClone(c.persons['person:ilan']!); alias.id = 'person:renamed'; alias.current.name = 'Somebody else'; alias.presentationKey = 'reskinned-alias';
    rejects(() => registerPerson(c, alias), 'renamed alias');
    rejects(() => bindLiving(c, 'remains:ilan', 'companion', 'Z04'), 'remains conversion');
    const samePresentation = structuredClone(c.persons['person:ilan']!); samePresentation.id = 'person:lookalike'; samePresentation.lineageKey = 'new-lineage';
    rejects(() => registerPerson(c, samePresentation), 'reused dead presentation');
    const revived = structuredClone(c); const p = revived.persons['person:ilan']!;
    p.lifecycle = 'active'; p.current.hp = 8; p.deathEventId = null; delete revived.remains['remains:ilan'];
    rejects(() => assertContinuityTransition(c, revived), 'revived identity mutation');
    c = changeRemains(c, 'remains:ilan', 'animated');
    equal(c.persons['person:ilan']!.lifecycle, 'dead', 'Animation restored the person');
    c = changeRemains(c, 'remains:ilan', 'destroyed');
    return rejects(() => changeRemains(c, 'remains:ilan', 'exposed'), 'destroyed remains');
  },
  CT02() {
    let c = promote(ledgerFixture(), 'person:ilan', 'legacy');
    const original = structuredClone(c.persons['person:ilan']!.original);
    c = bindLiving(c, 'person:ilan', 'legacy', 'Z04');
    c = transferItem(c, 'item:sword', { kind: 'person', id: 'person:ilan' }, { kind: 'person', id: 'person:nera' });
    c = transferItem(c, 'item:dose', { kind: 'person', id: 'person:ilan' }, { kind: 'consumed', id: 'rite:prepared-care' });
    c = reviseSurvivor(c, 'person:ilan', { id: 'played:oath-change', skill: 'surveying', oath: 'Protect canal workers', allegiance: 'REE' });
    const current = structuredClone(c.persons['person:ilan']!.current);
    const absent = demanifest(c, 'person:ilan');
    for (const field of ['skills', 'oaths', 'allegiances'] as const) {
      const tampered = structuredClone(absent); tampered.persons['person:ilan']!.current[field] = [];
      rejects(() => assertContinuityTransition(c, tampered), `stale ${field} while unmanifested`);
    }
    c = bindLiving(absent, 'person:ilan', 'legacy', 'Z03');
    equal(c.persons['person:ilan']!.current, current, 'Rebinding changed current state');
    equal(c.persons['person:ilan']!.original, original, 'Promotion audit changed');
    const respawn = structuredClone(c); respawn.persons['person:ilan']!.current = structuredClone(original!);
    return rejects(() => validateLedger(respawn), 'original-loadout respawn mutation');
  },
  CT03() {
    for (const dead of [false, true]) {
      let c = ledgerFixture();
      if (dead) c = killPerson(c, 'person:ilan', 'death:closure');
      const before = hash(c);
      rejects(() => transferItem(c, 'item:sword', { kind: 'person', id: 'person:nera' }, { kind: 'location', id: 'Z03:socket' }), 'wrong source');
      equal(hash(c), before, 'Failed transfer mutated the ledger');
      c = bequeath(c, 'item:sword', 'person:ilan');
      if (!dead) { c = promote(c, 'person:ilan', 'legacy'); assert(!c.persons['person:ilan']!.original!.inventory.includes('item:sword'), 'Promotion preceded transfer'); }
      equal(c.items['item:sword']!.allocation.kind, 'bequest', 'Bequest lost its allocation');
      rejects(() => bequeath(c, 'item:dose', 'person:ilan'), 'second bequest');
      rejects(() => allocateItem(c, { ...c.items['item:sword']!, id: 'item:copy' }), 'duplicate provenance');
      c = transferItem(c, 'item:sword', { kind: 'bequest', id: c.chronologyId }, { kind: 'person', id: 'person:nera' });
      equal(c.pendingBequest, null, 'Bequest remained pending after receipt');
    }
    for (const mutation of ['currency', 'quest-right', 'stack', 'filled-container']) {
      const c = ledgerFixture(); const item = c.items['item:sword']!;
      if (mutation === 'stack') item.units = 2;
      else if (mutation === 'filled-container') { item.kind = 'container'; item.contents = ['item:dose']; }
      else item.kind = mutation as 'currency' | 'quest-right';
      rejects(() => bequeath(c, item.id, 'person:ilan'), mutation);
    }
    return 'Duplicate objects, extra carry slots, invalid ownership and nonphysical bequests rejected atomically.';
  },
  CT04() {
    const c = promote(ledgerFixture(), 'person:ilan', 'legacy');
    const absent = c.persons['person:ilan']!;
    assert(absent.lifecycle === 'unmanifested' && absent.current.hp === 8, 'Absence became death or healing');
    const bound = bindLiving(c, absent.id, 'legacy', 'Z03');
    equal(bound.persons[absent.id]!.current, absent.current, 'Manifesting changed memory or possessions');
    rejects(() => bindLiving(bound, absent.id, 'legacy', 'Z07'), 'simultaneous second body');
    const dead = killPerson(bound, absent.id, 'death:later');
    return rejects(() => demanifest(dead, absent.id), 'dead-to-unmanifested');
  },
  CT05() {
    for (const dead of [false, true]) for (const pressure of [0, 2, 4, 8]) {
      let s = createRun(makeEnvelope(ledgerFixture(), 'reference'), ledgerFixture());
      s.political.pressure = { T01: pressure, T02: pressure, T03: pressure };
      if (dead) for (const id of Object.keys(s.continuity.persons)) s = recordDeath(s, id, `death:all:${id}`);
      equal(returnTransition(s.continuity.settlement), 'next-run', 'Threat outcome stopped Return');
    }
    equal(returnTransition('settled'), null, 'Settled chronology permits Return');
    // The same assertion rejects a deliberately threat-coupled causal mutant.
    const threatCausedReturn = (actorsAlive: boolean) => actorsAlive ? 'next-run' : null;
    return rejects(() => equal(threatCausedReturn(false), returnTransition('folded'), 'Threat-caused Return mutation'), 'threat-caused Return');
  },
  CT06() {
    const initial = createRun(makeEnvelope(ledgerFixture(), 'reference'), ledgerFixture());
    let s = initial;
    const before = strategicHash(s);
    const commands = [{ type: 'wait', ticks: 1000 }, { type: 'pause', paused: true }, { type: 'wait', ticks: 1000 }, { type: 'read', interactionId: 'bench:ledger' }, { type: 'rest' }, { type: 'pause', paused: false }] as const;
    for (const command of commands) s = reduceIntent(s, { tick: s.tick, sequence: s.lastSequence + 1, command });
    equal(strategicHash(s), before, 'Idle, pause or reading advanced strategy');
    const trade = { ...s.continuity.items['item:sword']!, allocation: { kind: 'person' as const, id: 'person:ilan' } };
    s.continuity.persons['person:ilan']!.location = 'Z03';
    s = reduceIntent(s, { tick: s.tick, sequence: s.lastSequence + 1, command: { type: 'trade', itemId: trade.id, sellerId: 'person:ilan', price: 3 } });
    equal(strategicHash(s), before, 'Ordinary trade advanced strategy');
    s = reduceIntent(s, { tick: s.tick, sequence: s.lastSequence + 1, command: { type: 'commit-milestone', milestoneId: 'bench:grain-agreement' } });
    assert(strategicHash(s) !== before, 'Authored milestone had no effect');
    rejects(() => reduceIntent(s, { tick: s.tick, sequence: s.lastSequence + 1, command: { type: 'commit-milestone', milestoneId: 'bench:grain-agreement' } }), 'farmed effect');
    const death = recordDeath(initial, initial.pcId, 'death:pc');
    assert(death.events.some(e => e.type === 'abandoned-run') && death.participation === 'ended', 'PC death did not invoke authored closure');
    return rejects(() => reduceIntent(death, { tick: 0, sequence: 0, command: { type: 'rest' } }), 'posthumous action');
  },
  CT07() {
    for (const adversity of ['dead', 'hostile', 'missing-object', 'missing-merchant', 'occupation', 'adverse-oath']) {
      let c = ledgerFixture(); const o = opportunity();
      o.initialFacts.push('porter-consents');
      if (adversity === 'dead') c = killPerson(c, 'person:nera', 'death:witness');
      if (adversity === 'hostile') o.initialFacts.push('hostile:person:nera');
      if (adversity === 'missing-object') o.steps[0]!.itemId = 'item:missing';
      if (adversity === 'missing-merchant') o.steps[0]!.merchantFact = 'merchant:available';
      if (adversity === 'occupation') o.initialFacts.push('occupation');
      if (adversity === 'adverse-oath') { o.steps[0]!.forbids.push('oath:refuse-porter'); o.initialFacts.push('oath:refuse-porter'); }
      const trace = solveEvidence(o, c);
      const result = executeWitness(o, c, trace);
      assert(result.facts.includes(o.goal) && result.money === 0, 'Authorized fallback did not pay its real cost');
      o.budget = 2;
      rejects(() => solveEvidence(o, c), `unaffordable ${adversity}`);
    }
    const o = opportunity(); o.requiresLegacy = true;
    return rejects(() => solveEvidence(o, ledgerFixture()), 'legacy observation without any continuant');
  },
  CT08() {
    const { continuity: c, envelope } = earnedHistory();
    assert(settlementEligibility(envelope, c).eligible, 'Valid fourth opening was ineligible');
    const invalids: Array<(c: Continuity) => void> = [
      c => { c.observations.pop(); },
      c => { c.closedRuns.pop(); },
      c => { c.closedRuns[0]!.rulesetId = 'd00-contract-bench'; },
      c => { c.observations[1]!.proofId = 'P-M1'; },
      c => { c.observations[0]!.actionId = 'read-journal'; },
      c => { c.closedRuns[1]!.actions.find(a => a.proofId === 'P-I1')!.method = 'comparison'; },
      c => { c.closedRuns[0]!.outcome = 'died'; },
      c => { c.observations[0]!.runId = 'run:2'; },
    ];
    for (const mutate of invalids) {
      const invalid = structuredClone(c); mutate(invalid);
      assert(!settlementEligibility({ ...envelope, continuityDigest: hash(invalid) }, invalid).eligible, 'Forged proof history unlocked settlement');
    }
    assert(!settlementEligibility({ ...envelope, runOrdinal: 99 }, c).eligible, 'Forged run counter unlocked settlement');
    for (const namespace of ['test', 'demo', 'debug', 'prototype'] as const) {
      const invalid = structuredClone(c); invalid.namespace = namespace; for (const r of invalid.closedRuns) r.namespace = namespace;
      assert(!settlementEligibility({ ...envelope, namespace, continuityDigest: hash(invalid) }, invalid).eligible, 'Nonproduction history unlocked settlement');
    }
    const early = structuredClone(c); early.closedRuns = early.closedRuns.slice(0, 2);
    return rejects(() => assert(settlementEligibility({ ...envelope, runOrdinal: 3, continuityDigest: hash(early) }, early).eligible, 'Early ending mutation'), 'third-run ending');
  },
  CT09() {
    let c = ledgerFixture(); c = promote(c, 'person:ilan', 'legacy');
    c.persons['person:nera']!.location = 'Z04'; c.persons['person:nera']!.current.reputation = ['Hostile to the current outsider'];
    c = promote(c, 'person:nera', 'supporting');
    const account = settlementAccount(c);
    equal(account.living.map(p => p.id), ['person:ilan', 'person:nera'], 'Hostile or supporting survivor omitted');
    assertCompleteAccount(c, account);
    const omitted = structuredClone(account); omitted.living.pop();
    rejects(() => assertCompleteAccount(c, omitted), 'omitted enemy');
    const duplicated = structuredClone(account); duplicated.living.push(duplicated.living[0]!);
    rejects(() => assertCompleteAccount(c, duplicated), 'duplicated person');
    c = killPerson(bindLiving(c, 'person:nera', 'legacy', 'Z04'), 'person:nera', 'death:enemy');
    const final = settlementAccount(c);
    equal(final.dead, ['person:nera'], 'Dead supporting person omitted from account');
    return rejects(() => assertCompleteAccount(c, account), 'restored dead person');
  },
  CT10() {
    const { continuity: c, envelope } = earnedHistory();
    const settled = settle(c, envelope, c, settlementAccount(c), { crisisAnswered: true, approachPrepared: true, crossingCompleted: true });
    equal(settled.date, FIRST_EMBER, 'Settlement date differs');
    equal(formatDate(settled.date), '1 Ember 641 AC', 'Rendered first date differs');
    equal(INTERVAL_DAYS, 42, 'Calendar interval differs');
    rejects(() => nextRunOrdinal(settled), 'settled next run');
    return rejects(() => formatDate({ day: 31, month: 'Press', year: 641 }), 'nonexistent date');
  },
};
export function runInvariant(id: string): InvariantResult {
  const metadata = COVERAGE.find(row => row[0] === id);
  if (!metadata || !checks[id]) throw new Error(`Unknown invariant ${id}`);
  try { return { id, title: metadata[1], valid: true, invalid: true, detail: checks[id](), outstanding: metadata[2] }; }
  catch (e) { return { id, title: metadata[1], valid: false, invalid: false, detail: e instanceof Error ? e.message : String(e), outstanding: metadata[2] }; }
}
export const runInvariants = (): InvariantResult[] => COVERAGE.map(([id]) => runInvariant(id));
